/**
 * Prediction: one screen, two states (DESIGN.md §3.4, §3.5).
 *
 * This file used to be a three-view wizard — pick first, pick last, review —
 * and that is reversed. Two picks are not two screens. The wizard spent four
 * screen states expressing two choices, made "change my last answer" cost a
 * trip back through the first, and then parked the predictor on a review screen
 * reading *"Both answers complete"* for the whole minute the turn player spends
 * dragging cards. It also did not look like the game: a two-column list of
 * avatar-and-name rows is a contact list, and this game is played with license
 * cards.
 *
 * What replaces it is one screen with two states, and the state is *this
 * player's own progress*, not the phase:
 *
 *  1. **Picking** — the question is the heading, two slots arm themselves
 *     `FIRST` then `LAST`, and the room sits underneath as a grid of faces.
 *     Tapping a face fills the armed slot; tapping a filled slot re-arms just
 *     that one, so replacing "last" never costs you "first". There is no
 *     *Change answers* button because the slots are the controls.
 *  2. **The room board** — pressing Submit locks this seat and performs the
 *     swap. The two slots stay pinned; the grid below becomes who the room is
 *     still waiting on, by face.
 *
 * ## What the countdown does and does not change
 *
 * The same component serves both prediction phases, and phase 3 adds a strip of
 * chrome and nothing else (DESIGN §3.5). In particular **zero does not swap the
 * body**: a player who never pressed Submit watches their own grid go inert
 * under an ink strip, in exactly the geometry it already had. The strip's whole
 * argument is that the screen beneath it does not move, and swapping the body
 * out at zero would be the one thing it forbids.
 *
 * Phase 3 may also never happen at all — every connected predictor locking
 * before the ranking lands sends the turn straight to the reveal — so nothing
 * here is staged on the countdown's arrival.
 *
 * ## Two messages, two meanings
 *
 * `submit_prediction` fires on every complete pair, before and after Submit is
 * ever pressed; `lock_prediction` fires once and says "stop waiting for me".
 * SPEC §2.3 penalises a **partial** pick, never an unsubmitted one, so a
 * complete pair scores whether or not this player ever locked in — which is
 * only true if the pair is on the server the moment it is complete.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PlayerId } from '@idgame/protocol';
import {
  Button,
  PredictionFaceGrid,
  PredictionRoomBoard,
  PredictionSlots,
  Screen,
  type PredictionSlot,
  type PredictionWait,
} from '../../components/index.js';
import {
  useLockPrediction,
  usePendingPredictors,
  usePhase,
  useQuestion,
  useRankablePlayers,
  useSelfPrediction,
  useSelfPredictionLocked,
  useSubmitPrediction,
  useTurnPlayer,
  useTurnPlayerId,
} from '../../store/index.js';

/** The two answers as the screen holds them, before either is complete. */
interface Picks {
  first: PlayerId | null;
  last: PlayerId | null;
}

export function PredictionFlow({
  strip,
  hardLocked = false,
  children,
}: {
  /** The countdown chrome, during phase 3 only — and phase 3 is optional. */
  strip?: ReactNode;
  /** Zero has passed on this device's reading of the server clock. */
  hardLocked?: boolean;
  children?: ReactNode;
}) {
  const roster = useRankablePlayers();
  const turnPlayer = useTurnPlayer();
  const turnPlayerId = useTurnPlayerId();
  const question = useQuestion();
  const phase = usePhase();
  const pending = usePendingPredictors();
  const { prediction, editable } = useSelfPrediction();
  const locked = useSelfPredictionLocked();
  const submitPrediction = useSubmitPrediction();
  const lockPrediction = useLockPrediction();

  const [picks, setPicks] = useState<Picks>({ first: null, last: null });
  /**
   * The slot the player asked to replace, or null to follow the picks.
   *
   * Armed is *derived* from this rather than stored outright, because the
   * ordinary case has no state in it at all: the first empty slot is armed,
   * `FIRST` before `LAST`, and nothing is armed once both are full. Only
   * "replace this one" is a decision, and it is spent by the next tap.
   */
  const [rearmed, setRearmed] = useState<PredictionSlot | null>(null);

  /*
    Adopt whatever the server holds. This is what makes a reconnect
    mid-prediction invisible (SPEC §5.4), and it is what keeps the phone from
    ever displaying a pair the server does not have. It never clears a pick:
    the server only ever sends complete pairs back.
  */
  useEffect(() => {
    if (!prediction) return;
    setPicks({ first: prediction.top, last: prediction.bottom });
  }, [prediction]);

  const complete = picks.first !== null && picks.last !== null;
  const canEdit = editable && !locked && !hardLocked;
  const armed: PredictionSlot | null = !canEdit
    ? null
    : (rearmed ?? (picks.first === null ? 'first' : picks.last === null ? 'last' : null));

  const byId = (id: PlayerId | null) => roster.find((player) => player.id === id) ?? null;

  function pick(id: PlayerId) {
    if (!armed) return;
    /*
      A tap always REPLACES. There is no gesture on this screen that empties a
      slot, and there must not be: the server has no "withdraw" message, so a
      phone that could show a half-answer would be showing something the server
      is about to score whole.
    */
    const next: Picks = armed === 'first' ? { ...picks, first: id } : { ...picks, last: id };
    // One player cannot hold both ends. The grid renders the other slot's
    // holder unpickable, so this is the same rule stated twice on purpose.
    if (next.first !== null && next.first === next.last) return;

    setPicks(next);
    setRearmed(null);
    // Sent the instant the pair is complete, and on every change after that.
    // A partial pick is never sent, which is how "partial picks earn nothing"
    // stays true (SPEC §2.3 phase 3).
    if (next.first && next.last) submitPrediction({ top: next.first, bottom: next.last });
  }

  /**
   * Everyone who is in, for the ticked row under the collapsed board.
   *
   * Derived rather than sent: the server tells predictors who is *pending*, and
   * the rest of the room is arithmetic. Disconnected seats are neither. They are
   * off the waiting list because a dark phone cannot act, not because it is
   * finished — whatever it had on record will still score (SPEC §5.4), but it
   * has not locked and may come back and keep editing, so a tick would claim a
   * decision nobody made.
   *
   * Self is on the ticked row the instant Submit is pressed, because
   * `usePendingPredictors` drops this seat as soon as it locks rather than
   * waiting for the server's next `prediction_count`.
   */
  const lockedIn = useMemo(() => {
    const waiting = new Set(pending.map((player) => player.id));
    return roster.filter(
      (player) => player.id !== turnPlayerId && player.connected && !waiting.has(player.id),
    );
  }, [roster, pending, turnPlayerId]);

  const who = turnPlayer?.name ?? 'them';

  /*
    What the board says the room is waiting for. The countdown only ever starts
    because the ranking was submitted, so from phase 3 onwards nobody is waiting
    on the turn player to rank — and past zero nobody is waiting on predictions
    either. Read off the phase rather than off the strip, because the strip is
    absent for one frame at the phase change and absent entirely on the skip path.
  */
  const wait: PredictionWait = hardLocked
    ? 'locked'
    : phase === 'lock-countdown'
      ? 'locking'
      : 'ranking';

  return (
    <Screen
      testId="predict-screen"
      strip={strip}
      eyebrow={`Predict ${who}`}
      meta={locked ? 'Locked in' : undefined}
      /*
        The question, and nothing else. It is set at 19px — the same treatment
        the turn player's ranking screen gives the same question, because it is
        the same question — and the "Who will Maya rank first?" ask line that
        used to sit under it is gone. That line explained a control that now
        explains itself: the lit slot is the question.
      */
      title={
        question ? (
          <span data-testid="predict-question" className="block text-[1.1875rem] leading-[1.2]">
            {question.number} · {question.text}
          </span>
        ) : undefined
      }
      /*
        The footer is always rendered, and holds the Submit button only when
        there is a pair to lock.

        It used to be conditional, which made the tap that landed the second pick
        insert ~76px of button and shrink all twelve faces in the same frame —
        and then the countdown strip arriving took another ~56px and shrank them
        again. This file's own header claims nothing moves when the answer lands,
        and DESIGN §3.5 says the same about the strip; reserving the space is what
        makes both true rather than aspirational.
      */
      footer={
        <div className="min-h-13">
          {complete && canEdit ? (
            <Button data-testid="predict-submit" onClick={lockPrediction}>
              Submit
            </Button>
          ) : null}
        </div>
      }
    >
      <PredictionSlots
        className="mb-3"
        first={byId(picks.first)}
        last={byId(picks.last)}
        armed={armed}
        locked={locked || !canEdit}
        {...(canEdit ? { onArm: setRearmed } : {})}
      />
      {locked ? (
        <PredictionRoomBoard
          pending={pending}
          turnPlayer={turnPlayer}
          locked={lockedIn}
          wait={wait}
        />
      ) : (
        <PredictionFaceGrid
          roster={roster}
          first={picks.first}
          last={picks.last}
          armed={armed}
          onPick={pick}
          disabled={!canEdit}
        />
      )}
      {children}
    </Screen>
  );
}
